import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import api from '../services/api';
import { translateUiText } from './translateUiText';
import { isRtlLanguage } from './RtlCacheProvider';

type TranslationMap = Record<string, string>;

export interface LangMeta {
  code: string;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
  flag: string;
  isDefault?: boolean;
}

const FALLBACK_LANGUAGES: LangMeta[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr', flag: '🇬🇧', isDefault: true },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl', flag: '🇸🇦' },
];

const FLAG_BY_CODE: Record<string, string> = {
  en: '🇬🇧', hi: '🇮🇳', ta: '🇮🇳', te: '🇮🇳', kn: '🇮🇳', ml: '🇮🇳', mr: '🇮🇳',
  bn: '🇧🇩', gu: '🇮🇳', pa: '🇮🇳', ur: '🇵🇰', ar: '🇸🇦', he: '🇮🇱', fa: '🇮🇷',
  fr: '🇫🇷', de: '🇩🇪', es: '🇪🇸', pt: '🇧🇷', it: '🇮🇹', zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷',
};

const LANG_STORAGE_KEY = 'appLanguage';

function toLangMeta(row: {
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  isDefault?: boolean;
}): LangMeta {
  return {
    code: String(row.code).toLowerCase(),
    label: row.name,
    nativeLabel: row.nativeName || row.name,
    dir: (row.rtl || isRtlLanguage(String(row.code))) ? 'rtl' : 'ltr',
    flag: FLAG_BY_CODE[String(row.code).toLowerCase()] || '🌐',
    isDefault: !!row.isDefault,
  };
}

interface I18nContextValue {
  language: string;
  langMeta: LangMeta;
  setLanguage: (lang: string) => void;
  t: (key: string, fallback?: string) => string;
  /** Translate known English UI copy (buttons, fields, headers) via Translator Master catalog. */
  tl: (english: string) => string;
  languages: string[];
  langList: LangMeta[];
  loading: boolean;
  dir: 'ltr' | 'rtl';
  reloadTranslations: () => Promise<void>;
  /** Re-fetch the active language list (call after a Language master is added, toggled or deleted). */
  reloadLanguages: () => Promise<void>;
}

const defaultLang = FALLBACK_LANGUAGES[0];

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  langMeta: defaultLang,
  setLanguage: () => undefined,
  t: (key, fallback) => fallback || key,
  tl: (english) => english,
  languages: FALLBACK_LANGUAGES.map((l) => l.code),
  langList: FALLBACK_LANGUAGES,
  loading: false,
  dir: 'ltr',
  reloadTranslations: async () => undefined,
  reloadLanguages: async () => undefined,
});

function resolveInitialLanguage(rows: LangMeta[]): string {
  const saved = localStorage.getItem(LANG_STORAGE_KEY)?.toLowerCase().trim();
  if (saved && rows.some((r) => r.code === saved)) return saved;
  const fromBackend = rows.find((r) => r.isDefault)?.code || rows[0]?.code || 'en';
  return fromBackend;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState(() => localStorage.getItem(LANG_STORAGE_KEY) || 'en');
  const [translations, setTranslations] = useState<TranslationMap>({});
  const [langList, setLangList] = useState<LangMeta[]>(FALLBACK_LANGUAGES);
  const [loading, setLoading] = useState(false);
  const bootstrapped = useRef(false);

  const langMeta = langList.find((l) => l.code === language) || langList[0] || defaultLang;
  // Prefer backend RTL flag; always force RTL for Arabic / Hebrew / Farsi / Urdu
  const dir: 'ltr' | 'rtl' = (langMeta.dir === 'rtl' || isRtlLanguage(language)) ? 'rtl' : 'ltr';

  const ensureTenant = () => {
    if (!localStorage.getItem('tenantSlug')) {
      localStorage.setItem('tenantSlug', 'demo');
    }
  };

  const loadTranslations = useCallback(async (lang: string) => {
    ensureTenant();
    setLoading(true);
    try {
      const res = await api.get(`/locale/public/translations/${lang}`);
      const map = (res.data.data?.translations || {}) as TranslationMap;
      setTranslations(map);

      // If non-English catalog is still mostly English, surface a gentle console hint
      // (Translator Master → Auto-translate continues filling remaining keys).
      if (lang !== 'en') {
        const values = Object.values(map);
        if (values.length > 20) {
          const sameAsKeyCount = values.filter((v) => typeof v === 'string' && /^[A-Za-z0-9 &\-_/.,'’:()+%]+$/.test(v)).length;
          const ratio = sameAsKeyCount / values.length;
          if (ratio > 0.85) {
            // eslint-disable-next-line no-console
            console.info(
              `[i18n] Language "${lang}" still has mostly English strings (${Math.round(ratio * 100)}%). ` +
                'Open Translator Master → Auto-translate to finish provisioning.',
            );
          }
        }
      }
    } catch {
      setTranslations({});
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLanguages = useCallback(async () => {
    ensureTenant();
    try {
      const res = await api.get('/locale/public/languages');
      const rows = res.data.data;
      if (Array.isArray(rows) && rows.length) {
        const mapped = rows.map(toLangMeta);
        setLangList(mapped);
        if (!bootstrapped.current) {
          bootstrapped.current = true;
          const resolved = resolveInitialLanguage(mapped);
          localStorage.setItem(LANG_STORAGE_KEY, resolved);
          setLanguageState((prev) => (prev === resolved ? prev : resolved));
        } else {
          setLanguageState((prev) => {
            if (mapped.some((r) => r.code === prev)) return prev;
            const resolved = resolveInitialLanguage(mapped);
            localStorage.setItem(LANG_STORAGE_KEY, resolved);
            return resolved;
          });
        }
        return;
      }
    } catch {
      /* fallback below */
    }
    setLangList(FALLBACK_LANGUAGES);
    bootstrapped.current = true;
  }, []);

  useEffect(() => {
    loadLanguages();
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadLanguages();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadLanguages]);

  useEffect(() => {
    loadTranslations(language);
  }, [language, loadTranslations]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', dir);
    document.documentElement.style.setProperty('direction', dir);
    document.body.style.setProperty('direction', dir);
  }, [dir, language]);

  const setLanguage = (lang: string) => {
    const code = lang.toLowerCase().trim();
    localStorage.setItem(LANG_STORAGE_KEY, code);
    setLanguageState(code);
  };

  const reloadTranslations = useCallback(async () => {
    await loadTranslations(language);
  }, [language, loadTranslations]);

  const t = useCallback((key: string, fallback?: string) => {
    if (!key) return fallback || '';
    if (translations[key]) return translations[key];
    // Support callers passing bare keys like "nav.dashboard" or "admin.nav.dashboard"
    const shortKey = key.includes('.') ? key.split('.').slice(1).join('.') : key;
    for (const [k, v] of Object.entries(translations)) {
      if (k === key || k.endsWith(`.${key}`) || k.endsWith(`.${shortKey}`) || k === shortKey) return v;
    }
    return fallback || key;
  }, [translations]);

  const tl = useCallback((english: string) => translateUiText(english, t), [t]);

  const value = useMemo(() => ({
    language,
    langMeta,
    setLanguage,
    t,
    tl,
    languages: langList.map((l) => l.code),
    langList,
    loading,
    dir,
    reloadTranslations,
    reloadLanguages: loadLanguages,
  }), [language, langMeta, t, tl, langList, loading, dir, reloadTranslations, loadLanguages]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Backward compatibility for components importing SUPPORTED_LANGUAGES
export const SUPPORTED_LANGUAGES = FALLBACK_LANGUAGES;
